/**
 * HomePage Component
 * Main page displaying regulations table and assessment functionality
 */

import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import { useTheme } from "@mui/material/styles";
import RegulationsTable from "../components/RegulationsTable";
import type { StartManualAssessmentFromTablePayload } from "../components/RegulationsTable/hooks";
import {
  buildConceptAssessmentResult,
  type StartManualAssessmentRegulationContext,
  type TriageImpactedItem,
} from "../services/manualAssessmentService";
import { AssessmentDialog } from "../components/AssessmentDialog";
import { useHasPrivilege } from "../hooks/useHasPrivilege";
import {
  selectThemeMode,
  selectUserRole,
  useAppStore,
  selectDomain,
  selectSelectedRequirementIds,
  selectRegulationsListPagination,
  selectAssessmentSidebarIds,
  selectAssessmentSidebarSelectedIds,
  selectAssessmentSidebarVersionOverrides,
} from "../stores";
import { apiClient } from "../services/apiClient";
import styles from "./HomePage.module.css";
import {
  AssessmentSidebarHost,
  DomainSelector,
  SelectImpactAssessmentPlanDialog,
  useAutoAssessmentDialog,
} from "./HomePage/index";
import { AssessmentCenterIcon } from "./HomePage/AssessmentCenterIcon";
import { useCompanyScopeImpactFlow } from "./HomePage/useCompanyScopeImpactFlow";
import {
  AIImpactAssessmentGuidanceMessage,
  AIImpactAssessmentResultsModal,
  CompanyScopeAlreadyAssessedDialog,
  isAiImpactGuidanceDismissed,
  StartManualAssessmentDialog,
  type AiResultsModalRow,
  type ManualAssessmentEntrySource,
} from "../components/AssessmentCenterPage";
import { useGlobalConfig } from "../hooks/useGlobalConfig";
import { useModuleDisplayName } from "../hooks/useModuleDisplayName";
import { postRegulationsNextHomeToParent } from "../utils/regulationsNextIframeHome";
import { useUserPermissions } from "../hooks/useUserPermissions";
import { useFreyrLibraryContext } from "../context/FreyrLibraryContext";
import { resolveAllowedDomain } from "./HomePage/constants";
import { useAllowedDomainOptions } from "./HomePage/useAllowedDomainOptions";
import { loadPersistedDomain } from "../utils/domainPersistence";
import { DEFAULT_THEME_AND_COLORS } from "../branding/constants/defaultBranding";
import { brandingDebug } from "../branding/utils/brandingDebug";
import { useBranding } from "../branding";

const DEFAULT_DOMAIN = "MPR";

function HomePage() {
  const navigate = useNavigate();
  const muiTheme = useTheme();
  const { config: brandingConfig } = useBranding();
  const queryClient = useQueryClient();
  const { config } = useGlobalConfig();
  const moduleDisplayName = useModuleDisplayName();
  const maxRegulationsPerBatch = config?.max_regulations_per_batch ?? 5;
  const { domain: contextDomain } = useFreyrLibraryContext();
  /** Host-provided domain (embed). */
  const hostDomain = contextDomain;

  const domain = useAppStore(selectDomain);
  const setDomain = useAppStore((state) => state.setDomain);
  const setSelectedRequirementIds = useAppStore(
    (state) => state.setSelectedRequirementIds,
  );
  const clearAssessmentSidebarIds = useAppStore(
    (state) => state.clearAssessmentSidebarIds,
  );
  const paginationModel = useAppStore(selectRegulationsListPagination);
  const setPaginationModel = useAppStore(
    (state) => state.setRegulationsListPagination,
  );
  const domainChangeSkipPaginationReset = useRef(true);
  const lastHostDomainRef = useRef<string | undefined>(undefined);
  const hasSeededDomainRef = useRef(false);
  const { options: allowedDomainOptions, privilegesLoaded: domainPrivilegesLoaded } =
    useAllowedDomainOptions();
  /**
   * Resolve the domain the user may actually view as soon as privileges load.
   * Avoids a frame where the store/session still says MPR (default) while the
   * user only has view_cos/mdv/... — that mismatch caused search to return
   * data that the grid never reliably showed.
   */
  const effectiveDomain = useMemo(() => {
    if (!domainPrivilegesLoaded) return domain;
    return resolveAllowedDomain(domain, allowedDomainOptions) ?? domain;
  }, [allowedDomainOptions, domain, domainPrivilegesLoaded]);
  // Privileges are loaded once globally by `PrivilegesBootstrap` in main.tsx.
  const canRunImpact = useHasPrivilege("gri.assessment.impact.execute");
  const canRunSystemImpact = useHasPrivilege(
    "gri.assessment.impact.system.execute",
  );
  const canReadChecklist = useHasPrivilege("gri.assessment.checklist");
  const canExecuteManualAssessment = useHasPrivilege(
    "gri.assessment.manual.execute",
  );

  const themeMode = useAppStore(selectThemeMode);
  const selectedRequirementIds = useAppStore(selectSelectedRequirementIds);
  const assessmentSidebarIds = useAppStore(selectAssessmentSidebarIds);
  const assessmentSidebarSelectedIds = useAppStore(
    selectAssessmentSidebarSelectedIds,
  );
  const assessmentSidebarVersionOverrides = useAppStore(
    selectAssessmentSidebarVersionOverrides,
  );

  const userRole = useAppStore(selectUserRole);
  const setUserRole = useAppStore((state) => state.setUserRole);
  const { allowedRoles } = useUserPermissions();
  const [assessDialogOpen, setAssessDialogOpen] = useState(false);
  const [selectPlanDialogOpen, setSelectPlanDialogOpen] = useState(false);
  const [planDialogSource, setPlanDialogSource] = useState<
    "sidebar" | "grid" | null
  >(null);
  /** When opening the plan dialog from a single grid row, use these ids instead of checkbox selection */
  const [gridPlanRegulationIdsOverride, setGridPlanRegulationIdsOverride] =
    useState<string[] | null>(null);
  const [aiGuidanceOpen, setAiGuidanceOpen] = useState(false);
  const [assessmentCenterAnchorEl, setAssessmentCenterAnchorEl] =
    useState<HTMLElement | null>(null);
  const [aiGuidanceVariant, setAiGuidanceVariant] = useState<
    "default" | "manual"
  >("default");
  const [startManualDialogOpen, setStartManualDialogOpen] = useState(false);
  const [startManualImpactedItems, setStartManualImpactedItems] = useState<
    readonly TriageImpactedItem[] | undefined
  >(undefined);
  const [startManualRegulation, setStartManualRegulation] = useState<
    StartManualAssessmentRegulationContext | undefined
  >(undefined);
  const [startManualConceptResult, setStartManualConceptResult] = useState<
    ReturnType<typeof buildConceptAssessmentResult> | null
  >(null);
  const [startManualEntrySource, setStartManualEntrySource] =
    useState<ManualAssessmentEntrySource>("direct");

  const handleStartManualAssessmentFromTable = useCallback(
    ({
      regulationId,
      regulationVersion,
      regulationTitle,
    }: StartManualAssessmentFromTablePayload) => {
      setStartManualRegulation({
        regulationId,
        regulationVersion,
        regulationTitle,
      });
      setStartManualImpactedItems(undefined);
      setStartManualConceptResult(null);
      setStartManualEntrySource("direct");
      setStartManualDialogOpen(true);
    },
    [],
  );

  const handleStartManualAssessmentFromAiResults = useCallback(
    (row: AiResultsModalRow) => {
      setStartManualRegulation({
        regulationId: row.regulationId,
        regulationVersion: row.version,
        regulationTitle: row.regulation || row.regulationId,
      });
      const items =
        row.impactedItems && row.impactedItems.length > 0
          ? [...row.impactedItems]
          : undefined;
      setStartManualImpactedItems(items);
      const level = (row.impactLevel ?? "no").trim();
      setStartManualConceptResult(
        items
          ? buildConceptAssessmentResult(
              row.regulationId,
              row.version,
              level.toLowerCase() !== "no" && level !== "—",
              level,
              row.reportUrl,
              items,
            )
          : null,
      );
      setStartManualEntrySource("from_ai_impact");
      setStartManualDialogOpen(true);
    },
    [],
  );

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    const cssPrimary = getComputedStyle(document.documentElement)
      .getPropertyValue("--brand-primary")
      .trim();
    brandingDebug("HomePage color snapshot", {
      brandingContextPrimary: brandingConfig.themeAndColors.primaryColor,
      brandingContextHeader: brandingConfig.themeAndColors.headerBackgroundColor,
      muiThemePrimaryMain: muiTheme.palette.primary.main,
      defaultConstantPrimary: DEFAULT_THEME_AND_COLORS.primaryColor,
      cssVarBrandPrimary: cssPrimary || "(empty)",
      mismatchConstantVsContext:
        DEFAULT_THEME_AND_COLORS.primaryColor.toLowerCase() !==
        brandingConfig.themeAndColors.primaryColor.toLowerCase(),
      mismatchConstantVsMui:
        DEFAULT_THEME_AND_COLORS.primaryColor.toLowerCase() !==
        muiTheme.palette.primary.main.toLowerCase(),
    });
  }, [brandingConfig.themeAndColors, muiTheme.palette.primary.main]);

  const isDark = themeMode === "dark";

  const handleShowSnackbar = useCallback(
    (state: {
      open: boolean;
      message: string;
      severity: "success" | "error" | "info";
    }) => {
      setSnackbar(state);
    },
    [],
  );

  const handleCloseSelectPlanDialog = useCallback(() => {
    setSelectPlanDialogOpen(false);
    setPlanDialogSource(null);
    setGridPlanRegulationIdsOverride(null);
  }, []);

  const selectedIdsForGrid = Array.isArray(selectedRequirementIds)
    ? selectedRequirementIds
    : [];

  const [regulationsListRefreshSignal, setRegulationsListRefreshSignal] =
    useState(0);

  const refreshAfterAssessment = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["assessment-center"] });
    setRegulationsListRefreshSignal((n) => n + 1);
  }, [queryClient]);

  const cancelCompanyScopeRegulationPollRef = useRef<() => void>(() => {});

  const autoAssessmentDialog = useAutoAssessmentDialog({
    open: selectPlanDialogOpen,
    assessmentSidebarIds,
    regulationIdsForVersionLookup:
      selectPlanDialogOpen && planDialogSource === "grid"
        ? (gridPlanRegulationIdsOverride ?? selectedIdsForGrid)
        : assessmentSidebarIds,
    runAssessmentIds:
      selectPlanDialogOpen && planDialogSource === "grid"
        ? (gridPlanRegulationIdsOverride ?? selectedIdsForGrid)
        : assessmentSidebarSelectedIds.length > 0
          ? assessmentSidebarSelectedIds
          : undefined,
    regulationVersionOverrides: assessmentSidebarVersionOverrides,
    onShowSnackbar: handleShowSnackbar,
    onCloseDialog: handleCloseSelectPlanDialog,
    onAiResultsModalClose: () => cancelCompanyScopeRegulationPollRef.current(),
    onCancelExternalResultsPoll: () =>
      cancelCompanyScopeRegulationPollRef.current(),
    onAssessmentComplete: refreshAfterAssessment,
  });

  const companyScopeImpact = useCompanyScopeImpactFlow({
    selectedIds: selectedIdsForGrid,
    maxRegulationsPerBatch,
    onShowSnackbar: handleShowSnackbar,
    openAiResultsModalWithRows: autoAssessmentDialog.openAiResultsModalWithRows,
    updateAiResultsModalRows: autoAssessmentDialog.updateAiResultsModalRows,
    setAiResultsTableLoading: autoAssessmentDialog.setAiResultsTableLoading,
    refreshAssessmentCenter: refreshAfterAssessment,
  });
  cancelCompanyScopeRegulationPollRef.current =
    companyScopeImpact.cancelRegulationStatusPolling;

  const sidebarCompanyScopeImpact = useCompanyScopeImpactFlow({
    selectedIds: assessmentSidebarSelectedIds,
    regulationVersionOverrides: assessmentSidebarVersionOverrides,
    maxRegulationsPerBatch,
    onShowSnackbar: handleShowSnackbar,
    openAiResultsModalWithRows: autoAssessmentDialog.openAiResultsModalWithRows,
    updateAiResultsModalRows: autoAssessmentDialog.updateAiResultsModalRows,
    setAiResultsTableLoading: autoAssessmentDialog.setAiResultsTableLoading,
    refreshAssessmentCenter: refreshAfterAssessment,
  });

  const handleOpenPlanDialogFromSidebar = useCallback(() => {
    setGridPlanRegulationIdsOverride(null);
    setPlanDialogSource("sidebar");
    setSelectPlanDialogOpen(true);
  }, []);

  const handleOpenCustomScopeFromGrid = useCallback(() => {
    setGridPlanRegulationIdsOverride(null);
    setPlanDialogSource("grid");
    setSelectPlanDialogOpen(true);
  }, []);

  const handleRowCompanyScopeImpact = useCallback(
    (regulationId: string) => {
      void companyScopeImpact.startCompanyScopeImpact([regulationId]);
    },
    [companyScopeImpact],
  );

  const handleRowCustomScopeImpact = useCallback((regulationId: string) => {
    setGridPlanRegulationIdsOverride([regulationId]);
    setPlanDialogSource("grid");
    setSelectPlanDialogOpen(true);
  }, []);

  const isDomainSwitchBlocked =
    assessDialogOpen ||
    selectPlanDialogOpen ||
    aiGuidanceOpen ||
    startManualDialogOpen ||
    autoAssessmentDialog.aiResultsModalOpen ||
    companyScopeImpact.partialDialogOpen ||
    companyScopeImpact.allDialogOpen ||
    sidebarCompanyScopeImpact.partialDialogOpen ||
    sidebarCompanyScopeImpact.allDialogOpen;

  const resetHomeSelectionForDomainChange = useCallback(() => {
    setSelectedRequirementIds([]);
    clearAssessmentSidebarIds();
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
  }, [
    clearAssessmentSidebarIds,
    paginationModel.pageSize,
    setPaginationModel,
    setSelectedRequirementIds,
  ]);

  const handleDomainChange = useCallback(
    (nextDomain: string) => {
      if (isDomainSwitchBlocked || nextDomain === domain) return;
      resetHomeSelectionForDomainChange();
      setDomain(nextDomain);
    },
    [
      domain,
      isDomainSwitchBlocked,
      resetHomeSelectionForDomainChange,
      setDomain,
    ],
  );

  // Sync userRole when allowedRoles loads - ensure current role is valid
  useEffect(() => {
    if (allowedRoles.length > 0 && !allowedRoles.includes(userRole)) {
      setUserRole(allowedRoles[0]);
    }
  }, [allowedRoles, userRole, setUserRole]);

  /**
   * Fusion / Shell integration:
   * When on HomePage, we should ensure the address bar is clean.
   * If modulePath is present, we remove it to prevent refreshing back to details.
   */
  useEffect(() => {
    const cleanUrlParams = () => {
      try {
        const topWin = window.top;
        if (topWin && topWin !== window.self) {
          const u = new URL(topWin.location.href);
          if (
            u.searchParams.has("modulePath") ||
            u.searchParams.has("version")
          ) {
            u.searchParams.delete("modulePath");
            u.searchParams.delete("version");
            topWin.history.replaceState(
              topWin.history.state,
              "",
              u.pathname + u.search + u.hash,
            );
          }
          // Also notify shell via postMessage as a backup
          postRegulationsNextHomeToParent();
        }

        // Clean current frame too
        const selfUrl = new URL(window.location.href);
        if (selfUrl.searchParams.has("modulePath")) {
          selfUrl.searchParams.delete("modulePath");
          window.history.replaceState(
            window.history.state,
            "",
            selfUrl.pathname + selfUrl.search + selfUrl.hash,
          );
        }
      } catch (e) {
        postRegulationsNextHomeToParent();
      }
    };

    cleanUrlParams();
  }, []);

  // Seed domain once:
  // - Initial mount: URL ?domain= > sessionStorage > host (when store still default)
  // - Host changes: only follow shell when user still matches previous host domain
  // Do not re-seed on every domain change — that fights privilege-based fallbacks
  // (e.g. session MPR when the user only has view_cos/mdv/chp/fds).
  useEffect(() => {
    const readDomainFromUrl = (): string | null => {
      try {
        const v = new URL(window.location.href).searchParams.get("domain");
        return v || null;
      } catch {
        return null;
      }
    };

    if (!hasSeededDomainRef.current) {
      hasSeededDomainRef.current = true;
      lastHostDomainRef.current = hostDomain;

      const restored = readDomainFromUrl() ?? loadPersistedDomain();
      if (restored) {
        setDomain(restored);
        return;
      }

      if (hostDomain && domain === DEFAULT_DOMAIN && domain !== hostDomain) {
        setDomain(hostDomain);
      }
      return;
    }

    // Host domain may arrive after first paint; remember it without treating as a switch.
    if (lastHostDomainRef.current === undefined && hostDomain) {
      lastHostDomainRef.current = hostDomain;
      return;
    }

    if (!hostDomain) return;

    if (lastHostDomainRef.current !== hostDomain) {
      if (isDomainSwitchBlocked) return;
      const previousHostDomain = lastHostDomainRef.current;
      lastHostDomainRef.current = hostDomain;
      if (domain === previousHostDomain && domain !== hostDomain) {
        resetHomeSelectionForDomainChange();
        setDomain(hostDomain);
      }
    }
  }, [
    hostDomain,
    domain,
    isDomainSwitchBlocked,
    resetHomeSelectionForDomainChange,
    setDomain,
  ]);

  // After privileges load, leave a domain the user cannot view (always — not blocked by dialogs).
  useEffect(() => {
    if (!domainPrivilegesLoaded) return;
    if (effectiveDomain && effectiveDomain !== domain) {
      resetHomeSelectionForDomainChange();
      setDomain(effectiveDomain);
    }
  }, [
    domain,
    domainPrivilegesLoaded,
    effectiveDomain,
    resetHomeSelectionForDomainChange,
    setDomain,
  ]);

  // Sync sessionStorage / API domain header with the effective (allowed) domain.
  useEffect(() => {
    if (!effectiveDomain) return;
    apiClient.setDomain(effectiveDomain);
  }, [effectiveDomain]);

  // Reset regulations list to first page when effective domain changes (not on first render)
  useEffect(() => {
    if (domainChangeSkipPaginationReset.current) {
      domainChangeSkipPaginationReset.current = false;
      return;
    }
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveDomain, setPaginationModel]);

  const handleCloseAssessDialog = useCallback(() => {
    setAssessDialogOpen(false);
  }, []);

  const handleAssessmentSuccess = useCallback((message: string) => {
    setSnackbar({
      open: true,
      message,
      severity: "success",
    });
  }, []);

  const handleAssessmentError = useCallback((message: string) => {
    setSnackbar({
      open: true,
      message,
      severity: "error",
    });
  }, []);

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleCloseAiResultsModal = useCallback(() => {
    autoAssessmentDialog.handleCloseAiResultsModal();
    if (!isAiImpactGuidanceDismissed()) {
      setAiGuidanceVariant("default");
      setAiGuidanceOpen(true);
    }
  }, [autoAssessmentDialog]);

  const shellTone = isDark ? styles.shellDark : styles.shellLight;
  const domainSwitchTitle = isDomainSwitchBlocked
    ? "Close the current dialog before switching domain."
    : undefined;

  return (
    <div className={`${styles.shell} ${shellTone}`}>
      <header className={styles.header}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <span
            style={{
              fontWeight: 600,
              fontSize: "20px",
              lineHeight: "20px",
            }}
          >
            {moduleDisplayName}
          </span>
          <DomainSelector
            domain={effectiveDomain}
            onDomainChange={handleDomainChange}
            isDark={isDark}
            disabled={isDomainSwitchBlocked}
            title={domainSwitchTitle}
          />
        </div>
        <div className={styles.headerActions}>
          <Button
            ref={setAssessmentCenterAnchorEl}
            variant="outlined"
            size="small"
            onClick={() => navigate("/assessment-center")}
            startIcon={<AssessmentCenterIcon />}  
            className={styles.assessmentCenterButton}
            sx={{
              textTransform: "none",
              backgroundColor: "#fff",
              color: "#000",
              borderColor: "#e5e5e5",
              "& .MuiButton-startIcon": {
                marginRight: "10px",
              },
              "&:hover": {
                backgroundColor: "#fff",
                borderColor: "#e5e5e5",
              },
            }}
          >
            Assessment Center
          </Button>
          <AIImpactAssessmentGuidanceMessage
            open={aiGuidanceOpen}
            variant={aiGuidanceVariant}
            placement="anchored"
            anchorEl={assessmentCenterAnchorEl}
            onClose={() => setAiGuidanceOpen(false)}
          />
        </div>
      </header>

      <main className={styles.main}>
        <section className={styles.workspace}>
          <div className={styles.column}>
            <div className={styles.noteCard}>
              <RegulationsTable
                domain={effectiveDomain}
                domainReady={domainPrivilegesLoaded}
                listRefreshSignal={regulationsListRefreshSignal}
                onCompanyScopeImpact={
                  canRunSystemImpact
                    ? companyScopeImpact.startCompanyScopeImpact
                    : undefined
                }
                onCustomScopeImpact={
                  canRunImpact && canReadChecklist
                    ? handleOpenCustomScopeFromGrid
                    : undefined
                }
                companyScopeLoadingRegulationIds={
                  companyScopeImpact.companyScopeLoadingRegulationIds
                }
                onRowCompanyScopeImpact={
                  canRunSystemImpact
                    ? handleRowCompanyScopeImpact
                    : undefined
                }
                onRowCustomScopeImpact={
                  canRunImpact && canReadChecklist
                    ? handleRowCustomScopeImpact
                    : undefined
                }
                onStartManualAssessment={
                  canExecuteManualAssessment
                    ? handleStartManualAssessmentFromTable
                    : undefined
                }
              />
            </div>
          </div>
        </section>

        <AssessmentSidebarHost
          assessmentSidebarIds={assessmentSidebarIds}
          assessmentSidebarSelectedIds={assessmentSidebarSelectedIds}
          regulationTitles={autoAssessmentDialog.regulationTitles}
          regulationVersions={autoAssessmentDialog.regulationVersions}
          regulationMarkets={autoAssessmentDialog.regulationMarkets}
          regulationProductTypes={autoAssessmentDialog.regulationProductTypes}
          canRender={
            (canRunImpact && canReadChecklist) || canRunSystemImpact
          }
          canRunCompanyScope={canRunSystemImpact}
          canRunCustomScope={canRunImpact && canReadChecklist}
          companyScopeLoadingRegulationIds={
            sidebarCompanyScopeImpact.companyScopeLoadingRegulationIds
          }
          onCompanyScopeImpact={(ids) => {
            void sidebarCompanyScopeImpact.startCompanyScopeImpact(ids);
          }}
          onCustomScopeImpact={handleOpenPlanDialogFromSidebar}
        />
      </main>

      <AssessmentDialog
        open={assessDialogOpen}
        selectedRequirementIds={
          Array.isArray(selectedRequirementIds) ? selectedRequirementIds : []
        }
        onClose={handleCloseAssessDialog}
        onSuccess={handleAssessmentSuccess}
        onError={handleAssessmentError}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {canReadChecklist && (
        <SelectImpactAssessmentPlanDialog
          open={selectPlanDialogOpen}
          onClose={handleCloseSelectPlanDialog}
          runLoading={autoAssessmentDialog.runAssessmentLoading}
          onRun={autoAssessmentDialog.handleRunAssessmentByPlan}
          runRegulationIds={
            planDialogSource === "grid"
              ? (gridPlanRegulationIdsOverride ?? selectedIdsForGrid)
              : assessmentSidebarSelectedIds
          }
        />
      )}

      <AIImpactAssessmentResultsModal
        open={autoAssessmentDialog.aiResultsModalOpen}
        rows={autoAssessmentDialog.aiResultsRows}
        loading={autoAssessmentDialog.aiResultsTableLoading}
        onClose={handleCloseAiResultsModal}
        onRetrySuccess={autoAssessmentDialog.resumeAiResultsPollAfterRerun}
        onOpenAssessmentCenter={() => navigate("/assessment-center")}
        onStartManualAssessment={
          canExecuteManualAssessment
            ? handleStartManualAssessmentFromAiResults
            : undefined
        }
      />

      <StartManualAssessmentDialog
        open={startManualDialogOpen}
        regulation={
          startManualRegulation ?? {
            regulationId: "",
            regulationVersion: "",
            regulationTitle: "",
          }
        }
        onClose={() => {
          setStartManualDialogOpen(false);
          setStartManualImpactedItems(undefined);
          setStartManualRegulation(undefined);
          setStartManualConceptResult(null);
          setStartManualEntrySource("direct");
        }}
        entrySource={startManualEntrySource}
        impactedItems={startManualImpactedItems}
        conceptAssessmentResult={startManualConceptResult}
        onInitiated={() => {
          companyScopeImpact.cancelRegulationStatusPolling();
          autoAssessmentDialog.handleCloseAiResultsModal();
          setAiGuidanceVariant("manual");
          setAiGuidanceOpen(true);
        }}
      />

      <CompanyScopeAlreadyAssessedDialog
        open={companyScopeImpact.partialDialogOpen}
        variant="partial"
        onClose={companyScopeImpact.closeCompanyScopeDialogs}
        alreadyCount={companyScopeImpact.pendingContext?.alreadyRun.length ?? 0}
        selectedTotal={companyScopeImpact.pendingContext?.selectedTotal ?? 0}
        alreadyRunRegulations={
          companyScopeImpact.pendingContext?.alreadyRun ?? []
        }
        onRunAnyway={companyScopeImpact.handlePartialRunAnyway}
        onSkipAlreadyRun={companyScopeImpact.handlePartialSkip}
        actionsDisabled={companyScopeImpact.companyScopeFlowLoading}
      />
      <CompanyScopeAlreadyAssessedDialog
        open={companyScopeImpact.allDialogOpen}
        variant="all"
        onClose={companyScopeImpact.closeCompanyScopeDialogs}
        alreadyCount={companyScopeImpact.pendingContext?.alreadyRun.length ?? 0}
        selectedTotal={companyScopeImpact.pendingContext?.selectedTotal ?? 0}
        alreadyRunRegulations={
          companyScopeImpact.pendingContext?.alreadyRun ?? []
        }
        onRunAnyway={companyScopeImpact.handleAllRunAnyway}
        actionsDisabled={companyScopeImpact.companyScopeFlowLoading}
      />
      <CompanyScopeAlreadyAssessedDialog
        open={sidebarCompanyScopeImpact.partialDialogOpen}
        variant="partial"
        onClose={sidebarCompanyScopeImpact.closeCompanyScopeDialogs}
        alreadyCount={
          sidebarCompanyScopeImpact.pendingContext?.alreadyRun.length ?? 0
        }
        selectedTotal={
          sidebarCompanyScopeImpact.pendingContext?.selectedTotal ?? 0
        }
        alreadyRunRegulations={
          sidebarCompanyScopeImpact.pendingContext?.alreadyRun ?? []
        }
        onRunAnyway={sidebarCompanyScopeImpact.handlePartialRunAnyway}
        onSkipAlreadyRun={sidebarCompanyScopeImpact.handlePartialSkip}
        actionsDisabled={sidebarCompanyScopeImpact.companyScopeFlowLoading}
      />
      <CompanyScopeAlreadyAssessedDialog
        open={sidebarCompanyScopeImpact.allDialogOpen}
        variant="all"
        onClose={sidebarCompanyScopeImpact.closeCompanyScopeDialogs}
        alreadyCount={
          sidebarCompanyScopeImpact.pendingContext?.alreadyRun.length ?? 0
        }
        selectedTotal={
          sidebarCompanyScopeImpact.pendingContext?.selectedTotal ?? 0
        }
        alreadyRunRegulations={
          sidebarCompanyScopeImpact.pendingContext?.alreadyRun ?? []
        }
        onRunAnyway={sidebarCompanyScopeImpact.handleAllRunAnyway}
        actionsDisabled={sidebarCompanyScopeImpact.companyScopeFlowLoading}
      />
    </div>
  );
}

export default HomePage;
